import {
  useGetApiVirtualDevices,
  useDeleteApiVirtualDevicesId,
  getGetApiVirtualDevicesQueryKey,
  type VirtualdevicesVirtualDeviceDTO,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  AlertCircle,
  Layers,
  Trash2,
  ChevronRight,
  X,
  Pencil,
  Plus,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { VirtualDeviceModal } from '@/features/virtual-devices/components/CreateVirtualDeviceModal';

/**
 * Virtual Devices management page
 */
export function VirtualDevicesPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useGetApiVirtualDevices();
  const deleteMutation = useDeleteApiVirtualDevicesId();

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deviceToDelete, setDeviceToDelete] = useState<VirtualdevicesVirtualDeviceDTO | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deviceToEdit, setDeviceToEdit] = useState<VirtualdevicesVirtualDeviceDTO | undefined>(
    undefined
  );

  const handleOpenCreate = () => {
    setDeviceToEdit(undefined);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (device: VirtualdevicesVirtualDeviceDTO) => {
    setDeviceToEdit(device);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setDeviceToEdit(undefined);
  };

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: getGetApiVirtualDevicesQueryKey() });
  };

  const handleDelete = async () => {
    if (!deviceToDelete?.id) return;
    try {
      await deleteMutation.mutateAsync({ id: deviceToDelete.id });
      invalidateQueries();
      setDeviceToDelete(null);
      if (expandedId === deviceToDelete.id) {
        setExpandedId(null);
      }
    } catch {
      // Error is handled by the mutation state
    }
  };

  const virtualDevices = data?.virtualDevices ?? [];

  return (
    <div className="space-y-8">
      {/* Header with back link */}
      <div>
        <Link
          to={ROUTES.DEVICES}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Devices
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Virtual Devices
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              Manage virtual devices that combine or route physical devices
            </p>
          </div>
          <button
            onClick={handleOpenCreate}
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-all hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
          >
            <Plus className="h-4 w-4" />
            Create
          </button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch virtual devices</h3>
              <p className="mt-1 text-sm text-red-400/70">
                There was an error loading virtual devices.
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {/* Virtual Devices list */}
      {!isLoading && !isError && (
        <div>
          <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
            Virtual Devices ({virtualDevices.length})
          </h2>
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
            {virtualDevices.length > 0 ? (
              virtualDevices.map((device, index) => (
                <VirtualDeviceCard
                  key={device.id}
                  device={device}
                  isFirst={index === 0}
                  isExpanded={expandedId === device.id}
                  onToggle={() =>
                    setExpandedId(expandedId === device.id ? null : (device.id ?? null))
                  }
                  onEdit={() => handleOpenEdit(device)}
                  onDelete={() => setDeviceToDelete(device)}
                />
              ))
            ) : (
              <div className="p-8 text-center">
                <Layers className="mx-auto h-8 w-8 text-gray-400 dark:text-zinc-600" />
                <p className="mt-3 text-sm text-gray-500 dark:text-zinc-500">No virtual devices</p>
                <p className="mt-1 text-xs text-gray-400 dark:text-zinc-600">
                  Click "Create" to add a virtual device
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deviceToDelete && (
        <DeleteConfirmModal
          title="Delete Virtual Device"
          message={
            <>
              Are you sure you want to delete{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {deviceToDelete.name}
              </span>
              ? This will also remove the associated device from the system.
            </>
          }
          onClose={() => setDeviceToDelete(null)}
          onConfirm={handleDelete}
          isLoading={deleteMutation.isPending}
        />
      )}

      {/* Create/Edit Modal */}
      <VirtualDeviceModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        virtualDevice={deviceToEdit}
      />
    </div>
  );
}

function VirtualDeviceCard({
  device,
  isFirst,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
}: {
  device: VirtualdevicesVirtualDeviceDTO;
  isFirst: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const kindLabel = device.kind === 'combined_switch' ? 'Combined Switch' : 'Routed Device';
  const kindColor =
    device.kind === 'combined_switch'
      ? 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400'
      : 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400';

  return (
    <div className={!isFirst ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''}>
      {/* Header row */}
      <div className="flex items-center justify-between p-4">
        <button onClick={onToggle} className="flex flex-1 items-center gap-3 text-left">
          <ChevronRight
            className={`h-4 w-4 text-gray-400 transition-transform dark:text-zinc-600 ${
              isExpanded ? 'rotate-90' : ''
            }`}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[15px] font-medium text-gray-900 dark:text-white">
                {device.name}
              </span>
              <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${kindColor}`}>
                {kindLabel}
              </span>
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500">
              <span className="font-mono text-xs">{device.humanId}</span>
              <span>·</span>
              <span>{device.type}</span>
            </div>
          </div>
        </button>
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-blue-500/10 hover:text-blue-500 dark:text-zinc-500 dark:hover:text-blue-400"
            title="Edit"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-red-500/10 hover:text-red-500 dark:text-zinc-500 dark:hover:text-red-400"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Expanded details section */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50/50 px-4 pb-4 pt-3 dark:border-white/[0.06] dark:bg-white/[0.02]">
          <div className="space-y-3">
            <div>
              <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                ID
              </span>
              <p className="mt-1 font-mono text-sm text-gray-900 dark:text-white">{device.id}</p>
            </div>
            <div>
              <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                Configuration
              </span>
              <pre className="mt-1 overflow-auto rounded-lg bg-gray-100 p-3 text-xs text-gray-800 dark:bg-zinc-800 dark:text-zinc-200">
                {JSON.stringify(device.config, null, 2)}
              </pre>
            </div>
            {device.createdAt && (
              <div>
                <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                  Created
                </span>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {new Date(device.createdAt).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DeleteConfirmModal({
  title,
  message,
  onClose,
  onConfirm,
  isLoading,
}: {
  title: string;
  message: React.ReactNode;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-white/[0.06] dark:bg-[#18181b]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
              <p className="text-sm text-gray-500 dark:text-zinc-500">
                This action cannot be undone.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="mt-4 text-sm text-gray-600 dark:text-zinc-400">{message}</p>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-white/[0.06] dark:text-zinc-300 dark:hover:bg-white/[0.03]"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
          >
            {isLoading && (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            )}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
